import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TreeView from '../shared/TreeView';
import {
  displaySDFInfo,
  displaySDFTitleInfo,
  isScopeAnyGlobalType
} from '../shared/UtilFunctions';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormHelperText,
  IconButton,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Search } from '@deere/fuel-react/Search.js';
import {
  SCOPE_LEVEL_GLOBAL_AFTERMARKET,
  SCOPE_LEVEL_GLOBAL_PRODUCTION,
  SCOPE_LEVEL_UNITORUNITGROUP
} from '../shared/AppConstants';
import WarningErrorIcon from './WarningErrorIcon';
import {
  selectDisabledSideName,
  selectMasterScopeAftermarket,
  selectMasterScopeProduction,
  selectScopeDirty,
  selectScopeInvalidError,
  selectScopeType,
  selectSelectedScopeUnits,
  selectSelectedScopeViewList,
  setDisabledSideName,
  setMasterScopeAftermarket,
  setMasterScopeProduction,
  setScopeDirty,
  setScopeInvalidError,
  setScopeType,
  setSelectedScopeUnits
} from './Strategy/scopeSlice';

/**
 * Global scope has two mutually-exclusive sides. Given one side, returns the
 * other (the one that should be disabled); returns null for any non-global side.
 */
function oppositeSide(side) {
  if (side === SCOPE_LEVEL_GLOBAL_PRODUCTION) {
    return SCOPE_LEVEL_GLOBAL_AFTERMARKET;
  }
  if (side === SCOPE_LEVEL_GLOBAL_AFTERMARKET) {
    return SCOPE_LEVEL_GLOBAL_PRODUCTION;
  }
  return null;
}

export default function MasterScope(props) {
  const {
    masterScopeJson,
    register,
    isEdit,
    hasUSScope,
    strategyInformation,
    viewScope,
    getMissingSDFDisplay,
    isNewForm
  } = props;
  const dispatch = useDispatch();
  const selectedScopeUnits = useSelector(selectSelectedScopeUnits);
  const invalidError = useSelector(selectScopeInvalidError);
  const selectedScopeViewList = useSelector(selectSelectedScopeViewList);
  const masterScopeProduction = useSelector(selectMasterScopeProduction);
  const masterScopeAftermarket = useSelector(selectMasterScopeAftermarket);
  const disabledSideName = useSelector(selectDisabledSideName);
  const scopeType = useSelector(selectScopeType);
  const showScopeWarning = useSelector(selectScopeDirty);
  const [scopeFilterValue, setScopeFilterValue] = useState('');
  const [clearAllScopeTreeFlag, setClearAllScopeTreeFlag] = useState(false);
  // The persisted scope is stored in API shape ({ unitGroups, units }); the
  // TreeView consumes a tree shape ({ parent, children }), so adapt it here.
  const selectedScopeAsTree = selectedScopeViewList
    ? {
        level: selectedScopeViewList.level,
        parent: selectedScopeViewList.unitGroups || [],
        children: selectedScopeViewList.units || []
      }
    : null;
  const effectiveScopeType = scopeType || selectedScopeViewList?.level;

  function translateScopeType(scopeType) {
    return scopeType
      ? {
          level: SCOPE_LEVEL_UNITORUNITGROUP,
          parent: [scopeType],
          children: []
        }
      : { parent: [], children: [] };
  }
  let safeSelectedScopeList = isScopeAnyGlobalType(effectiveScopeType)
    ? translateScopeType(effectiveScopeType)
    : selectedScopeAsTree;

  function processGroupsJson(groupJson, parent) {
    const processedGroups = groupJson?.map((group) => {
      if (group.units) {
        return {
          ...group,
          children: [...group.units]
        };
      }
      return { ...group };
    });

    return {
      code: `${parent}`,
      name: `${parent}`,
      children: processedGroups
    };
  }

  function sanitizeScopeNodesForStore(scopeNodes) {
    if (!scopeNodes || scopeNodes.length === 0) {
      return [];
    }

    return scopeNodes.map((node) => ({
      code: node.code,
      name: node.name,
      checked: !!node.checked,
      children: sanitizeScopeNodesForStore(node.children)
    }));
  }

  function findScopeSideFromSelection() {
    if (isScopeAnyGlobalType(scopeType)) {
      return scopeType;
    }

    const selectedParents = [...(selectedScopeViewList?.unitGroups || [])];

    if (selectedParents.includes(SCOPE_LEVEL_GLOBAL_PRODUCTION)) {
      return SCOPE_LEVEL_GLOBAL_PRODUCTION;
    }
    if (selectedParents.includes(SCOPE_LEVEL_GLOBAL_AFTERMARKET)) {
      return SCOPE_LEVEL_GLOBAL_AFTERMARKET;
    }

    const selectedParentCodes = new Set(selectedParents);
    const selectedUnitCodes = new Set([
      ...(selectedScopeViewList?.units || [])
    ]);

    selectedScopeUnits?.forEach((group) => {
      if (group?.checked && group.code) {
        selectedParentCodes.add(group.code);
      }
      group?.children?.forEach((unit) => {
        if (unit?.checked && unit.code) {
          selectedUnitCodes.add(unit.code);
        }
      });
    });

    const globalLevelMap = masterScopeJson?.globalLevelMap;
    if (!globalLevelMap) return null;

    const productionGroups =
      globalLevelMap[SCOPE_LEVEL_GLOBAL_PRODUCTION] || [];
    const aftermarketGroups =
      globalLevelMap[SCOPE_LEVEL_GLOBAL_AFTERMARKET] || [];

    const sideHasSelection = (groups) =>
      groups.some((group) => {
        if (selectedParentCodes.has(group.code)) return true;
        return group.units?.some((unit) => selectedUnitCodes.has(unit.code));
      });

    const productionHasSelection = sideHasSelection(productionGroups);
    const aftermarketHasSelection = sideHasSelection(aftermarketGroups);

    if (productionHasSelection && !aftermarketHasSelection) {
      return SCOPE_LEVEL_GLOBAL_PRODUCTION;
    }
    if (aftermarketHasSelection && !productionHasSelection) {
      return SCOPE_LEVEL_GLOBAL_AFTERMARKET;
    }
    return null;
  }

  function hasAnyScopeSelection() {
    const hasPersistedSelection =
      (Array.isArray(selectedScopeViewList?.unitGroups) &&
        selectedScopeViewList.unitGroups.length > 0) ||
      (Array.isArray(selectedScopeViewList?.units) &&
        selectedScopeViewList.units.length > 0);

    if (hasPersistedSelection) {
      return true;
    }

    if (!Array.isArray(selectedScopeUnits) || selectedScopeUnits.length === 0) {
      return false;
    }

    return selectedScopeUnits.some(
      (group) =>
        group?.checked ||
        (Array.isArray(group?.children) &&
          group.children.some((unit) => unit?.checked))
    );
  }

  useEffect(() => {
    if (masterScopeJson?.globalLevelMap) {
      const globalProductionTree = processGroupsJson(
        masterScopeJson.globalLevelMap[SCOPE_LEVEL_GLOBAL_PRODUCTION],
        SCOPE_LEVEL_GLOBAL_PRODUCTION
      );
      const globalAfterMarketTree = processGroupsJson(
        masterScopeJson.globalLevelMap[SCOPE_LEVEL_GLOBAL_AFTERMARKET],
        SCOPE_LEVEL_GLOBAL_AFTERMARKET
      );

      dispatch(setMasterScopeProduction([globalProductionTree]));
      dispatch(setMasterScopeAftermarket([globalAfterMarketTree]));
      return;
    }
    dispatch(setMasterScopeProduction(null));
    dispatch(setMasterScopeAftermarket(null));
  }, [masterScopeJson]);

  useEffect(() => {
    let nextDisabledSideName;

    if (!scopeType) {
      nextDisabledSideName = null;
    } else if (isScopeAnyGlobalType(scopeType)) {
      nextDisabledSideName = oppositeSide(scopeType);
    } else {
      const scopeSideFromSelection = findScopeSideFromSelection();

      if (scopeSideFromSelection) {
        nextDisabledSideName = oppositeSide(scopeSideFromSelection);
      } else if (hasAnyScopeSelection() && disabledSideName) {
        // Preserve currently-locked side when selection still exists but
        // side inference is temporarily ambiguous (e.g. partial tree updates).
        nextDisabledSideName = disabledSideName;
      } else {
        nextDisabledSideName = null;
      }
    }

    if (nextDisabledSideName !== disabledSideName) {
      dispatch(setDisabledSideName(nextDisabledSideName));
    }
  }, [
    dispatch,
    scopeType,
    selectedScopeUnits,
    selectedScopeViewList,
    masterScopeJson,
    disabledSideName
  ]);

  function localSetParentScopeData(value, treeDirty) {
    const selectedScopeType = value?.[0]?.code;
    const anyChkBoxSelected = value?.[0]?.checked || value?.[0]?.indeterminate;

    const localDisabledSideName = anyChkBoxSelected
      ? oppositeSide(selectedScopeType)
      : null;
    let parentScopeType = null;
    let parentScopeData = null;

    if (anyChkBoxSelected) {
      if (value?.[0]?.checked) {
        parentScopeType = selectedScopeType;
      } else {
        parentScopeType = SCOPE_LEVEL_UNITORUNITGROUP;
        parentScopeData = sanitizeScopeNodesForStore(value[0].children);
      }
    }

    if (!treeDirty) {
      // Initialize scope units/type from TreeView's first reconciliation so
      // repeated view/edit toggles don't lose scope when no manual edits occur.
      if (!selectedScopeUnits && parentScopeType) {
        dispatch(setDisabledSideName(localDisabledSideName));
        dispatch(setScopeType(parentScopeType));
        dispatch(setSelectedScopeUnits(parentScopeData));
      }
      return;
    }

    dispatch(setDisabledSideName(localDisabledSideName));
    dispatch(setScopeType(parentScopeType));
    dispatch(setSelectedScopeUnits(parentScopeData));
    dispatch(setScopeDirty(true));
  }

  function clearBothScopeTrees() {
    setClearAllScopeTreeFlag(true);
    dispatch(setDisabledSideName(null));
    dispatch(setScopeType(null));
    dispatch(setSelectedScopeUnits(null));
    dispatch(setScopeDirty(true));
    dispatch(setScopeInvalidError('At least 1 Unit must be selected'));
  }

  return (
    <Box mt={isNewForm ? 0 : 3}>
      <Card>
        <CardContent>
          <Box>
            <Grid container alignItems="flex-start">
              <Grid size={isNewForm ? 12 : 8}>
                <Grid container alignItems="center" columnGap={1}>
                  {!isNewForm && isEdit && (
                    <IconButton
                      aria-label="edit"
                      onClick={(e) => viewScope(e)}
                      sx={(theme) => ({
                        borderRadius: theme.fuel6Tokens.JddcRadiusSmall,
                        flexShrink: 0,
                        ...(isEdit && { bgcolor: 'action.selected' })
                      })}
                    >
                      <fuel-icon name="edit" />
                    </IconButton>
                  )}
                  <Typography variant="h3">
                    Scope{' '}
                    <Box
                      component="span"
                      aria-label="required field"
                      sx={{ color: 'error.main' }}
                    >
                      *
                    </Box>
                    {!isNewForm && isEdit && (
                      <Box
                        component="span"
                        sx={{ color: 'text.secondary', ml: 1 }}
                      >
                        (Edit Mode)
                      </Box>
                    )}
                    <WarningErrorIcon state={props.duplicateStrategyState} />
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box>
            <Grid container alignItems="flex-start">
              <Grid size={12} sx={!isNewForm && isEdit ? { pl: 8 } : undefined}>
                {showScopeWarning && isEdit && !isNewForm && (
                  <Alert
                    severity="warning"
                    sx={(theme) => ({
                      mb: theme.fuel6Tokens.JddcSpacingMedium
                    })}
                  >
                    <Typography variant="bodySmall">
                      Any units / unit groups updated in the Scope will also be
                      updated in the supplier table along with their unique
                      statuses.
                    </Typography>
                  </Alert>
                )}

                {props.missingScopeError && (
                  <Typography variant="bodySmall" color="error">
                    This is a required field!
                  </Typography>
                )}

                {masterScopeProduction && masterScopeAftermarket && (
                  <>
                    <Box
                      sx={(theme) => ({
                        mt: theme.fuel6Tokens.JddcSpacingSmall,
                        mb: theme.fuel6Tokens.JddcSpacingSmall,
                        width: '100%'
                      })}
                    >
                      <Search
                        size="small"
                        fullWidth
                        id="scope-tree-filter-search"
                        placeholder="Filter this list"
                        slotProps={{
                          search: {
                            name: 'scope-search',
                            value: scopeFilterValue,
                            onChange: (e) => {
                              setScopeFilterValue(
                                e.target.value.trim().toLowerCase()
                              );
                            }
                          }
                        }}
                      />
                    </Box>
                    <Box
                      sx={(theme) => ({
                        width: '100%',
                        ...(invalidError && {
                          p: theme.fuel6Tokens.JddcSpacingMedium,
                          border: '1px solid',
                          borderColor: 'error.main',
                          borderRadius: theme.fuel6Tokens.JddcRadiusSmall
                        })
                      })}
                    >
                      <Grid
                        container
                        columnSpacing={2}
                        sx={{ width: '100%' }}
                        alignItems="flex-start"
                      >
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TreeView
                            key={SCOPE_LEVEL_GLOBAL_PRODUCTION}
                            readOnly={
                              disabledSideName === SCOPE_LEVEL_GLOBAL_PRODUCTION
                            }
                            invalidError={null}
                            masterInputTreeJson={masterScopeProduction}
                            showInOneColumn={true}
                            showFilter={false}
                            filterPlaceHolder="Filter this list"
                            selectAll={true}
                            createScope={true}
                            register={register}
                            setParentMasterData={localSetParentScopeData}
                            rpcOrUnitsFromParent={safeSelectedScopeList}
                            isEdit={isEdit}
                            expandAll={true}
                            filterValue={scopeFilterValue}
                            donotShowClearButton={true}
                            clearAllFlag={clearAllScopeTreeFlag}
                            setParentClearAllFlag={setClearAllScopeTreeFlag}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TreeView
                            key={SCOPE_LEVEL_GLOBAL_AFTERMARKET}
                            readOnly={
                              disabledSideName ===
                              SCOPE_LEVEL_GLOBAL_AFTERMARKET
                            }
                            invalidError={null}
                            masterInputTreeJson={masterScopeAftermarket}
                            showInOneColumn={true}
                            showFilter={false}
                            filterPlaceHolder="Filter this list"
                            selectAll={true}
                            createScope={true}
                            register={register}
                            setParentMasterData={localSetParentScopeData}
                            rpcOrUnitsFromParent={safeSelectedScopeList}
                            isEdit={isEdit}
                            expandAll={true}
                            filterValue={scopeFilterValue}
                            donotShowClearButton={true}
                            clearAllFlag={clearAllScopeTreeFlag}
                            setParentClearAllFlag={setClearAllScopeTreeFlag}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    {invalidError && (
                      <FormHelperText error>{invalidError}</FormHelperText>
                    )}
                    <Box
                      sx={(theme) => ({
                        mt: theme.fuel6Tokens.JddcSpacingMedium,
                        display: 'flex',
                        justifyContent: 'flex-end'
                      })}
                    >
                      <Button variant="secondary" onClick={clearBothScopeTrees}>
                        Clear
                      </Button>
                    </Box>
                  </>
                )}

                {invalidError && !masterScopeProduction && (
                  <FormHelperText error>{invalidError}</FormHelperText>
                )}

                {!isNewForm && isEdit && strategyInformation && (
                  <Box sx={{ mt: 1 }}>
                    <Box component="div">
                      {displaySDFTitleInfo(hasUSScope, strategyInformation)}
                    </Box>
                    <Box component="div" sx={{ mt: 0.5 }}>
                      {displaySDFInfo(
                        hasUSScope,
                        strategyInformation,
                        false,
                        isEdit,
                        getMissingSDFDisplay
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
